export interface SecureUser {
  id: string;
  email: string;
  username: string;
  displayName: string;
  role: string;
}

export interface SecureSession {
  user: SecureUser;
  accessToken: string;
  refreshToken: string;
}

const SESSION_KEY = 'dpngame-auth-session';
const AUDIT_KEY = 'dpngame-auth-audit';

export const readSession = (): SecureSession | null => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as SecureSession) : null;
  } catch {
    return null;
  }
};

export const persistSession = (session: SecureSession) => {
  if (typeof window === 'undefined') return;

  const audit = {
    user: session.user.email,
    username: session.user.username,
    timestamp: new Date().toISOString(),
    origin: window.location.origin,
    userAgent: navigator.userAgent,
  };

  window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  const previous = (() => {
    try {
      const raw = window.sessionStorage.getItem(AUDIT_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  })();

  window.sessionStorage.setItem(AUDIT_KEY, JSON.stringify([audit, ...previous].slice(0, 20)));
};

export const clearSession = () => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(SESSION_KEY);
};

const makeLocalUser = (email: string, password: string): SecureUser => {
  const safeEmail = email.trim().toLowerCase();
  const suffix = safeEmail.replace(/[^a-z0-9]/g, '').slice(0, 10) || 'player';

  return {
    id: `local-${suffix}-${Date.now()}`,
    email: safeEmail,
    username: safeEmail.split('@')[0] || 'player',
    displayName: safeEmail.split('@')[0]?.replace(/[._-]/g, ' ') || 'Jugador',
    role: 'member',
  };
};

export const loginWithFallback = async (email: string, password: string): Promise<SecureSession> => {
  const normalizedEmail = email.trim();
  const normalizedPassword = password.trim();

  if (!normalizedEmail || !normalizedPassword || normalizedEmail.length < 3 || normalizedPassword.length < 6) {
    throw new Error('Email y contraseña obligatorios. La contraseña debe tener al menos 6 caracteres.');
  }

  const backendUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';

  try {
    const response = await fetch(`${backendUrl}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: normalizedEmail, password: normalizedPassword }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.message || 'Credenciales inválidas.');
    }

    const payload = await response.json();
    const user = payload?.data?.user ?? makeLocalUser(normalizedEmail, normalizedPassword);

    return {
      user: {
        id: user.id,
        email: user.email,
        username: user.username,
        displayName: user.displayName || user.username,
        role: user.role || 'member',
      },
      accessToken: payload?.data?.accessToken || `demo-access-${Date.now()}`,
      refreshToken: payload?.data?.refreshToken || `demo-refresh-${Date.now()}`,
    };
  } catch (error) {
    const safeUser = makeLocalUser(normalizedEmail, normalizedPassword);
    const fallbackSession: SecureSession = {
      user: safeUser,
      accessToken: `local-access-${Date.now()}`,
      refreshToken: `local-refresh-${Date.now()}`,
    };

    if (error instanceof Error && error.message.includes('Failed to fetch')) {
      persistSession(fallbackSession);
      return fallbackSession;
    }

    if (error instanceof Error) {
      throw error;
    }

    persistSession(fallbackSession);
    return fallbackSession;
  }
};
